import React from 'react';
import { useLocation } from 'react-router';
import { Link, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
// `Link as ExternalLink` is intentionally the raw MUI component, not the ~/Atomic/Link
// wrapper: here it is passed as IconButton's polymorphic `component`, so ButtonBase
// injects DOM/event props (onKeyDown, tabIndex, role, ...) that the wrapper's
// prop-validation guard would flag.
import { Drawer, Link as ExternalLink } from '@mui/material';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import SidebarActionCreateButton from '~/components/core/Buttons/SidebarActionCreateButton';
import { getFnolInboxPath } from '~/components/Fnol/FnolAiInbox/utils';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import SupportMenu from '~/components/SupportMenu/SupportMenu';
import Avatar from '~/components/ui/atoms/Avatar';
import Divider from '~/components/ui/atoms/Divider';
import usePopoverState from '~/components/ui/atoms/Popover/usePopoverState';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import cn from '~/Utils/cn';

import { MIXPANEL_EVENTS } from '../pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '../Types';
import { isSysConfigEditor, isUserFiveSigma, isUserOshaRep, isUserSuperUser } from '../UserUtils';
import { isDashboardViewer, isExtraDashboardViewer, isFeatureEnabled, isLegacyOrgFeatureEnabled } from '../Utils';

import mixpanel from './CmsMain/mixpanel';
import { isPermissionsEnabled } from './core/Permissions/PermissionUtils';
import { useCms } from './hooks/useCms';
import { useIsReportViewer } from './hooks/useIsReportViewer';
import UserMenu from './UserMenu/index';
import { OrganizationLogo, PERMISSION_ACTIONS, PERMISSION_VERBS, PermissionsRenderWrapper } from './core';
import {
  AddIcon,
  AdvancedSearchIcon,
  ApprovalPaymentIcon,
  BuildingsIcon,
  CalendarIcon,
  ChecksIcon,
  ClaimsIcon,
  CommunicationsIcon,
  ContactNewIcon,
  DashboardReportsIcon,
  HomeIcon,
  LossReportIcon,
  MailIcon,
  MedicalBillsIcon,
  OshaReportsIcon,
  PoliciesIcon,
  QaReviewIcon,
  QuestionCircleIcon,
  RecoveriesIcon,
  SystemConfigurationIcon,
} from './icons';
import useOrganization from './OrganizationContext';

import { useStyles } from '../assets/styles';
import colors from '../assets/colors.module.scss';

// TODO - Phone Rep view
function CmsDrawer({ onUpdatePassword }) {
  const { user, userOrganization } = useCms();
  const { orgAdditionalConfigs, isPerformanceQaEnabled, isGeneralEmailQueueEnabled, isMetabaseEnabled } =
    useOrganization();
  const { classes } = useStyles();
  const location = useLocation();
  const history = useHistory();
  const {
    isApprovalPaymentsScreenEnabled,
    isPaymentsSubmissionScreenEnabled,
    isContactFinanceApprovalTabEnabled,
    isContactTfnApprovalTabEnabled,
  } = usePaymentsConfiguration();
  const glChecksPageUsers = orgAdditionalConfigs?.gl_checks_page_users || [];

  const userMenu = usePopoverState();
  const supportMenu = usePopoverState();

  const isUserReportsViewer = useIsReportViewer();

  const getDrawerOptions = () => {
    if (isUserFiveSigma(user)) {
      return [
        {
          tooltip: 'Home',
          Icon: HomeIcon,
          isActive: location.pathname === '/home',
          linkTo: '/home',
          shouldAppear: true,
        },
        {
          tooltip: 'Metabase',
          Icon: DashboardReportsIcon,
          isActive: location.pathname === '/admin/metabase',
          linkTo: '/admin/metabase',
          shouldAppear: isUserSuperUser(user),
        },
        {
          tooltip: 'System Configuration',
          Icon: SystemConfigurationIcon,
          isActive: location.pathname.startsWith('/admin') && !location.pathname.startsWith('/admin/metabase'),
          linkTo: '/admin',
          shouldAppear: isUserSuperUser(user),
        },
      ];
    }

    if (isUserOshaRep(user)) {
      return [];
    }

    const displayQaDashboard =
      isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.QA_DASHBOARD) || isPerformanceQaEnabled;
    return [
      { tooltip: 'Home', Icon: HomeIcon, isActive: location.pathname === '/home', linkTo: '/home', shouldAppear: true },
      {
        tooltip: 'Claims',
        Icon: ClaimsIcon,
        isActive: location.pathname.startsWith('/claims'),
        linkTo: '/claims',
        shouldAppear: true,
        permissions: { action: PERMISSION_ACTIONS.CLAIM, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Calendar',
        Icon: CalendarIcon,
        isActive: location.pathname === '/calendar',
        linkTo: '/calendar',
        shouldAppear: true,
      },
      {
        tooltip: 'Contacts',
        Icon: ContactNewIcon,
        isActive: location.pathname === '/contacts',
        linkTo: '/contacts',
        shouldAppear: true,
        permissions: { action: PERMISSION_ACTIONS.CONTACT, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Recoveries',
        Icon: RecoveriesIcon,
        isActive: location.pathname === '/recoveries',
        linkTo: '/recoveries',
        shouldAppear: true,
      },
      {
        tooltip: 'Performance QA Center',
        Icon: QaReviewIcon,
        isActive: location.pathname === '/qa-dashboard',
        linkTo: '/qa-dashboard',
        shouldAppear:
          isPermissionsEnabled(userOrganization) ||
          ((user.is_org_level_supervisor || user.is_manager) && displayQaDashboard),
        permissions: { action: PERMISSION_ACTIONS.QA, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Classification',
        Icon: MailIcon,
        isActive: location.pathname === '/classification',
        linkTo: '/classification',
        shouldAppear: isLegacyOrgFeatureEnabled(
          userOrganization,
          LEGACY_ORG_FEATURES.INSHUR_US_MGM_PHYSICAL_MAIL_CLASSIFICATION
        ),
      },
      {
        tooltip: 'Loss Report',
        Icon: LossReportIcon,
        isActive: location.pathname === '/loss_report',
        linkTo: '/loss_report',
        shouldAppear: isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.LOSS_REPORT_TEMP),
      },
      {
        tooltip: 'Approvals',
        Icon: ApprovalPaymentIcon,
        isActive: location.pathname.startsWith('/finance_approvals'),
        linkTo: '/finance_approvals',
        shouldAppear:
          isApprovalPaymentsScreenEnabled ||
          isPaymentsSubmissionScreenEnabled ||
          isContactFinanceApprovalTabEnabled ||
          isContactTfnApprovalTabEnabled,
      },
      {
        tooltip: 'Communication',
        Icon: CommunicationsIcon,
        isActive: location.pathname === '/communications',
        linkTo: '/communications',
        shouldAppear: true,
      },
      {
        tooltip: 'Medical Bills',
        Icon: MedicalBillsIcon,
        isActive: location.pathname === '/medical_bills',
        linkTo: '/medical_bills',
        shouldAppear: isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_MEDICAL_BILLS_PAGE),
      },
      {
        tooltip: 'Checks',
        Icon: ChecksIcon,
        isActive: location.pathname === '/organization_checks',
        linkTo: '/organization_checks',
        shouldAppear: isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_CHECKS_PAGE),
      },
      {
        tooltip: 'GL Checks',
        Icon: ChecksIcon,
        isActive: location.pathname === '/organization_gl_checks',
        linkTo: '/organization_gl_checks',
        shouldAppear:
          isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_CHECKS_PAGE) &&
          glChecksPageUsers.includes(user.id),
      },
      {
        tooltip: 'Osha Reports',
        Icon: OshaReportsIcon,
        isActive: location.pathname === '/osha_reports',
        linkTo: '/osha_reports',
        shouldAppear: isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_RW_OSHA_REPORTS),
      },
      {
        tooltip: 'System Configuration',
        Icon: SystemConfigurationIcon,
        isActive: location.pathname.startsWith('/sysconfig'),
        linkTo: '/sysconfig',
        shouldAppear:
          isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.PERMISSIONS_ENFORCEMENT) ||
          isSysConfigEditor(user),
        permissions: { action: PERMISSION_ACTIONS.SYSTEM_CONFIGURATION, verb: PERMISSION_VERBS.FULL },
      },
      {
        tooltip: 'Advanced Search',
        Icon: AdvancedSearchIcon,
        isActive: location.pathname === '/advanced_search',
        linkTo: '/advanced_search',
        shouldAppear: isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.HOSPITALITY_ADVANCED_SEARCH),
      },
      {
        tooltip: 'Policies',
        Icon: PoliciesIcon,
        isActive: location.pathname === '/policies',
        linkTo: '/policies',
        shouldAppear: isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.POLICIES_SEARCH_PAGE),
      },
      {
        tooltip: 'General Email Classification',
        Icon: MailIcon,
        isActive: location.pathname === '/general_email_classification',
        linkTo: '/general_email_classification',
        shouldAppear: isGeneralEmailQueueEnabled,
        permissions: {
          action: PERMISSION_ACTIONS.GENERAL_EMAIL_CLASSIFICATION,
          verb: PERMISSION_VERBS.READ,
        },
      },
      {
        tooltip: 'Reports',
        Icon: DashboardReportsIcon,
        isActive: location.pathname.startsWith('/reports'),
        linkTo: '/reports',
        shouldAppear:
          isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.FF_TABLEAU_REPORTS) &&
          !isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED) &&
          (isUserReportsViewer ||
            isDashboardViewer(orgAdditionalConfigs, user) ||
            isExtraDashboardViewer(orgAdditionalConfigs, user)),
        permissions: { action: PERMISSION_ACTIONS.REPORT, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Reports',
        Icon: DashboardReportsIcon,
        isActive: location.pathname.startsWith('/reports_v2'),
        linkTo: '/reports_v2',
        shouldAppear:
          isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.FF_TABLEAU_SELF_SERVICE) &&
          !isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED) &&
          isUserReportsViewer,
        permissions: { action: PERMISSION_ACTIONS.REPORT, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Reports V3',
        Icon: DashboardReportsIcon,
        isActive: location.pathname.startsWith('/reports_v3'),
        linkTo: '/reports_v3',
        shouldAppear:
          isMetabaseEnabled && !isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED),
        permissions: { action: PERMISSION_ACTIONS.REPORT, verb: PERMISSION_VERBS.READ },
      },
      {
        tooltip: 'Reports',
        Icon: DashboardReportsIcon,
        isActive: location.pathname === '/reports' || location.pathname.startsWith('/reports/'),
        linkTo: '/reports',
        shouldAppear:
          isMetabaseEnabled && isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.METABASE_APPROVED),
        permissions: { action: PERMISSION_ACTIONS.REPORT, verb: PERMISSION_VERBS.READ },
      },
    ].map((item) => {
      if (!item.permissions?.verb) {
        return {
          ...item,
          permissions: { action: item.permissions?.action, verb: PERMISSION_VERBS.READ },
        };
      }
      return item;
    });
  };

  const drawerOptions = getDrawerOptions();

  const isUserOutOfOffice = user.is_out_of_office;
  return (
    <>
      <Drawer
        variant="permanent"
        className={isUserOutOfOffice ? classes.OutOfOfficeDrawer : classes.drawer}
        classes={{ paper: isUserOutOfOffice ? classes.OutOfOfficeDrawer : classes.drawer }}
      >
        <div className={classes.drawerIconsContainer}>
          <Avatar
            {...userMenu.triggerProps}
            aria-label="User menu"
            className="mb-[10px] mt-4 border border-solid border-white/30 bg-teal-600/80 hover:border-white/70"
            src={`/api/v1/organizations/${user.organization_id}/users/${user.id}/avatar`}
          >
            {user.first_name[0]}
            {user.last_name[0]}
          </Avatar>
          <DrawerDivider />
          {isUserSuperUser(user) && (
            <>
              <SidebarActionCreateButton
                icon={BuildingsIcon}
                title="Add Org"
                onClick={() => history.push('/admin/organizations/new_organization_wizard')}
              />
              <DrawerDivider />
            </>
          )}
          {userOrganization.fnol_enabled &&
            isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL) && (
              <PermissionsRenderWrapper action={PERMISSION_ACTIONS.CREATE_FNOL} verb={PERMISSION_VERBS.WRITE}>
                <SidebarActionCreateButton
                  icon={AddIcon}
                  title="FNOL"
                  onClick={() => history.push(getFnolInboxPath())}
                  disabled={user.role.is_view_only}
                />
                <DrawerDivider />
              </PermissionsRenderWrapper>
            )}
          {drawerOptions
            .filter(({ shouldAppear }) => shouldAppear)
            .map(({ tooltip, Icon, isActive, linkTo, permissions, href }, idx) => (
              <CmsDrawerIconButtonWithPermissions
                tooltip={tooltip}
                Icon={Icon}
                isActive={isActive}
                linkTo={linkTo}
                permissions={permissions}
                href={href}
                key={idx}
              />
            ))}
        </div>
        <div className={classes.drawerButtonIcons}>
          <DrawerDivider />
          <CmsDrawerIconButton tooltip="Support" Icon={QuestionCircleIcon} {...supportMenu.triggerProps} />

          <OrganizationLogo />
        </div>
      </Drawer>

      <UserMenu
        anchorEl={userMenu.popoverProps.anchorEl}
        onClose={userMenu.close}
        id={userMenu.popoverProps.id}
        onUpdatePassword={onUpdatePassword}
      />
      <SupportMenu
        anchorEl={supportMenu.popoverProps.anchorEl}
        onClose={supportMenu.close}
        id={supportMenu.popoverProps.id}
      />
    </>
  );
}

const CmsDrawerIconButtonWithPermissions = ({ permissions, ...rest }) => {
  return (
    <PermissionsRenderWrapper {...permissions} useDefaults>
      <CmsDrawerIconButton {...rest} />
    </PermissionsRenderWrapper>
  );
};

CmsDrawerIconButtonWithPermissions.propTypes = {
  permissions: PropTypes.object.isRequired,
  tooltip: PropTypes.string,
  Icon: PropTypes.func,
  isActive: PropTypes.bool,
  linkTo: PropTypes.string,
  href: PropTypes.string,
};

// `ariaProps` carries a popover trigger's aria-haspopup, aria-expanded and aria-controls.
const CmsDrawerIconButton = ({ tooltip, Icon, isActive, linkTo, href, onClick, ...ariaProps }) => {
  const { classes } = useStyles();

  let CustomComponent;
  if (href) {
    CustomComponent = ExternalLink;
  } else if (linkTo) {
    CustomComponent = Link;
  }

  return (
    <Tooltip title={tooltip} placement="right" arrow>
      <IconButton
        component={CustomComponent}
        target={href ? '_blank' : ''}
        to={linkTo}
        href={href}
        className={cn(classes.drawerIconButton, { [classes.drawerIconButtonSelected]: isActive })}
        {...ariaProps}
        onClick={(e) => {
          mixpanel?.track(MIXPANEL_EVENTS.SIDE_PANEL_CLICK, { element: tooltip });
          if (onClick) {
            onClick(e);
          }
        }}
      >
        <Icon size={24} iconColor={isActive ? 'white' : colors.drawerIcon} />
      </IconButton>
    </Tooltip>
  );
};

CmsDrawerIconButton.propTypes = {
  tooltip: PropTypes.string,
  Icon: PropTypes.func,
  onClick: PropTypes.func,
  isActive: PropTypes.bool,
  linkTo: PropTypes.string,
  href: PropTypes.string,
};

const DrawerDivider = () => <Divider className="my-[10px] w-full border-white/[8%]" />;

CmsDrawer.propTypes = {
  onUpdatePassword: PropTypes.func,
};

export default CmsDrawer;
